import { useEffect, useRef, useState, useCallback } from "react";
import { Viewer } from "@photo-sphere-viewer/core";
import "@photo-sphere-viewer/core/index.css";
import { Maximize2, Minimize2, RotateCcw, Compass } from "lucide-react";
import ViewerFallback from "@/components/ViewerFallback";
import InteractionHintOverlay from "@/components/InteractionHintOverlay";
import { useHasGyroscope } from "@/lib/useHasGyroscope";
import { hasWebGLSupport } from "@/lib/webgl";
import { useViewerTracking } from "@/lib/viewerTracking";

type Props = {
    src: string;
    testIdPrefix?: string;
    /** Startet direkt nach Panorama-Ready die Auto-Tour (Yaw-Rotation
     *  ~12°/s + sanfte Pitch-Wave). Default false. Stoppt bei jeder
     *  User-Interaktion (pointerdown/touchstart/wheel/keydown). */
    autoTour?: boolean;
    /** Analytics: stabile Media-ID (aus DB). Wenn nicht gesetzt: hash(src). */
    mediaId?: string;
    mediaTitle?: string;
    projectId?: string;
    /** Wenn true: Shell-Klassen nutzen `w-full h-full` statt fixem
     *  Aspect-Ratio. Parent-Container gibt Größe vor. Default false. */
    fillParent?: boolean;
};

/**
 * 360° Foto Viewer (Photo Sphere Viewer, WebGL-basiert).
 *
 * WICHTIG (Nachbesserung nach User-Report):
 * KEIN Vorab-`isWebGLAvailable()`-Gate mehr. Wir versuchen direkt zu
 * initialisieren und fangen mit try/catch ab. Grund: die Vorab-Prüfung
 * lieferte in Chrome (Windows/Nvidia) false negatives, obwohl WebGL
 * tatsächlich verfügbar war.
 *
 * QA-Flag `window.__HF_FORCE_NO_WEBGL__` wird respektiert.
 */
export default function Photo360Viewer({
    src,
    testIdPrefix = "photo360",
    autoTour = false,
    mediaId,
    mediaTitle,
    projectId,
    fillParent = false,
}: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const viewerHostRef = useRef<HTMLDivElement | null>(null);
    const viewerRef = useRef<Viewer | null>(null);
    // Analytics: 360°-Foto-Tracking (Open, Interaction, Fullscreen, Close).
    const tracker = useViewerTracking({
        viewerType: "photo360",
        mediaId,
        mediaTitle: mediaTitle || src,
        projectId,
    });
    // Auto-Tour-Prop als Ref spiegeln, damit die viewer-Init-useEffect nicht
    // von `autoTour` als Dep abhängt (würde bei Toggle neu bauen).
    const autoTourRef = useRef<boolean>(autoTour);
    useEffect(() => {
        autoTourRef.current = autoTour;
    }, [autoTour]);
    // Auto-Rotate-State: nach Reset läuft ein rAF-Loop, der yaw kontinuierlich
    // rotiert (~12°/s) mit Sinus-Pitch-Wave (±3° über 8s). Bei jeder User-
    // Interaktion sofort stoppen.
    const autoRotatingRef = useRef<boolean>(false);
    const rafRef = useRef<number | null>(null);
    const [autoRotating, setAutoRotating] = useState<boolean>(false);

    const stopAutoRotate = useCallback(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        autoRotatingRef.current = false;
        setAutoRotating(false);
    }, []);

    // Gyroscope: Toggle über deviceorientation-Listener. Bei ON schreibt der
    // Handler in viewer.rotate({ yaw, pitch }); manueller Drag läuft parallel
    // (PSV eigenes Handling), was in Praxis okay ist — beim Loslassen des
    // Fingers übernimmt Gyro wieder.
    const gyroActiveRef = useRef<boolean>(false);
    const gyroHandlerRef = useRef<((e: DeviceOrientationEvent) => void) | null>(null);
    const [gyroOn, setGyroOn] = useState<boolean>(false);
    const showGyro = useHasGyroscope();

    // Wiederverwendbarer Gyro-Detach: für toggleGyro (OFF), Mutual-Exclusion-
    // Guard in startAutoRotate und Unmount-Cleanup. useCapture=true muss beim
    // Remove symmetrisch sein.
    const detachGyro = useCallback(() => {
        if (gyroHandlerRef.current) {
            window.removeEventListener(
                "deviceorientation",
                gyroHandlerRef.current,
                true,
            );
            gyroHandlerRef.current = null;
        }
        gyroActiveRef.current = false;
        setGyroOn(false);
    }, []);

    const toggleGyro = useCallback(async () => {
        if (gyroActiveRef.current) {
            detachGyro();
            return;
        }
        const DOE = window.DeviceOrientationEvent as unknown as {
            requestPermission?: () => Promise<"granted" | "denied">;
        };
        if (DOE && typeof DOE.requestPermission === "function") {
            try {
                const state = await DOE.requestPermission();
                if (state !== "granted") {
                    setGyroOn(false);
                    return;
                }
            } catch {
                setGyroOn(false);
                return;
            }
        }
        // Mutual Exclusion: Auto-Rotate läuft? → erst stoppen, damit rAF und
        // Gyro nicht gegeneinander laufen.
        if (autoRotatingRef.current) {
            stopAutoRotate();
        }
        const handler = (ev: DeviceOrientationEvent) => {
            if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
            const yaw = (((360 - ev.alpha) % 360) * Math.PI) / 180;
            const pitchDeg = Math.max(-85, Math.min(85, ev.beta - 90));
            const pitch = (pitchDeg * Math.PI) / 180;
            try {
                viewerRef.current?.rotate({ yaw, pitch });
            } catch {
                /* PSV noch nicht bereit — nächster Event wird's tun */
            }
        };
        window.addEventListener("deviceorientation", handler, true);
        gyroHandlerRef.current = handler;
        gyroActiveRef.current = true;
        setGyroOn(true);
    }, [detachGyro, stopAutoRotate]);

    // Cleanup bei Unmount — kein Handler darf über die Lebensdauer hinaus
    // bestehen bleiben.
    useEffect(() => {
        return () => {
            detachGyro();
        };
    }, [detachGyro]);

    const startAutoRotate = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        // Accessibility: prefers-reduced-motion → kein Auto-Rotate
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            console.log("[Photo360Viewer] auto-rotate skipped (prefers-reduced-motion)");
            return;
        }
        // Mutual Exclusion: Gyro aktiv? → sofort abschalten, sonst überschreibt
        // der deviceorientation-Handler die rAF-Kamera bei jedem Sensor-Event.
        if (gyroActiveRef.current) {
            detachGyro();
        }
        autoRotatingRef.current = true;
        setAutoRotating(true);
        const t0 = performance.now();
        const YAW_RAD_PER_S = (12 * Math.PI) / 180;        // 12°/s ≈ 2rpm
        const PITCH_AMP_RAD = (3 * Math.PI) / 180;          // ±3°
        const PITCH_PERIOD_S = 8;                            // 8s Wave
        const tick = (now: number) => {
            if (!autoRotatingRef.current) return;
            const elapsed = (now - t0) / 1000;
            const ease = Math.min(elapsed / 1.0, 1);        // 1s ease-in
            const yaw = ((YAW_RAD_PER_S * ease * elapsed) % (2 * Math.PI));
            const pitch = PITCH_AMP_RAD * Math.sin((elapsed * 2 * Math.PI) / PITCH_PERIOD_S);
            try {
                viewerRef.current?.rotate({ yaw, pitch });
            } catch {
                autoRotatingRef.current = false;
                setAutoRotating(false);
                return;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    }, [detachGyro]);

    // Cleanup rAF bei Unmount
    useEffect(() => {
        return () => stopAutoRotate();
    }, [stopAutoRotate]);

    // User-Interaction-Stop: mousedown/touchstart/wheel/keydown auf Shell.
    // Ausnahme: Klicks/Touches auf die Control-Bar selbst (Reset/Fullscreen/…)
    // sollen NICHT als "User dreht Kamera"-Interaktion zählen, sonst würde
    // der Reset-Toggle sofort in stopAutoRotate() laufen und der 2. Klick
    // startet die Rotation erneut statt sie zu beenden.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const stopEvents = ["pointerdown", "mousedown", "touchstart", "wheel"];
        const handler = (e: Event) => {
            const t = e.target as HTMLElement | null;
            if (t && t.closest(".hf-viewer-controls")) return;
            if (autoRotatingRef.current) stopAutoRotate();
            // Analytics: User rotiert/zoomt am Panorama.
            // Der Hook debounced auf 5 s / 30° selbst.
            try {
                const pos = viewerRef.current?.getPosition?.();
                const yawDeg = pos ? Math.round((pos.yaw * 180) / Math.PI) : undefined;
                const pitchDeg = pos ? Math.round((pos.pitch * 180) / Math.PI) : undefined;
                const zoom = viewerRef.current?.getZoomLevel?.();
                tracker.onInteraction(yawDeg, pitchDeg, typeof zoom === "number" ? Math.round(zoom) : undefined);
            } catch {
                tracker.onInteraction();
            }
        };
        stopEvents.forEach((ev) => el.addEventListener(ev, handler, { passive: true }));
        return () => stopEvents.forEach((ev) => el.removeEventListener(ev, handler));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [status, setStatus] = useState<"loading" | "ready" | "error">(
        "loading",
    );
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        const host = viewerHostRef.current;
        if (!host) return;

        const forced = Boolean(
            (window as unknown as { __HF_FORCE_NO_WEBGL__?: boolean })
                .__HF_FORCE_NO_WEBGL__,
        );
        if (forced) {
            setStatus("error");
            setErrorMsg("WebGL wurde per QA-Flag deaktiviert");
            return;
        }
        // Early guard vor jeglicher Three.js/PSV-Init: WebGL prüfen.
        // Verhindert Runtime-Crash bei fehlender GPU/Hardware-Beschleunigung.
        if (!hasWebGLSupport()) {
            setStatus("error");
            setErrorMsg(
                "WebGL ist in diesem Browser nicht verfügbar. Bitte moderneren Browser nutzen oder Hardware-Beschleunigung aktivieren.",
            );
            return;
        }

        // ---- CORS-safe Panorama-Loading ----
        // Der Preview-Overlay wrapped `window.fetch` und fügt credentials:'include'
        // hinzu. Damit erfordert R2 zusätzlich `Access-Control-Allow-Credentials`
        // — was CF R2 in unserer CORS-Config nicht sendet.
        // Workaround: Wir laden das Bild selbst per `<img crossOrigin="anonymous">`
        // (bypassed den fetch-Wrapper komplett) und konvertieren es zu einer
        // ObjectURL / DataURL, die wir dann an Photo Sphere Viewer geben.
        // PSV lädt dann nur die (Same-Origin-)Blob-URL, keine CORS-Prüfung nötig.
        let viewer: Viewer | null = null;
        let objectUrl: string | null = null;
        let cancelled = false;
        // Loading-Watchdog: Wenn nach 12s weder `panorama-loaded` noch ein
        // Fehler kommt, gehen wir in den error-State mit klarer Meldung —
        // besser als ewig „Panorama wird geladen…" anzuzeigen.
        const loadingTimeout = window.setTimeout(() => {
            if (cancelled) return;
            console.error(
                "[Photo360Viewer] Loading-Timeout (12s) — Panorama wurde nicht geladen. src=",
                src,
            );
            setStatus("error");
            setErrorMsg(
                "Zeitüberschreitung beim Laden des Panoramas (>12s). Bitte Verbindung prüfen oder Seite neu laden.",
            );
        }, 12000);

        const initWithBlobUrl = async () => {
            let panoramaSrc: string = src;
            try {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.decoding = "async";
                await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error("Image-Load fehlgeschlagen"));
                    img.src = src;
                });
                // Bild-Ratio-Check (Editorial-Fallback bei nicht-equirekt)
                const ratio = img.naturalWidth / img.naturalHeight;
                if (Math.abs(ratio - 2.0) > 0.05) {
                    console.warn(
                        `[Photo360Viewer] Bild hat kein equirektangulares Seitenverhältnis (${img.naturalWidth}×${img.naturalHeight}, ratio=${ratio.toFixed(3)})`,
                    );
                    if (!cancelled) {
                        setStatus("error");
                        setErrorMsg(
                            `Das Bild ist kein 360°-Panorama (Seitenverhältnis ${ratio.toFixed(2)} statt 2.00).`,
                        );
                    }
                    return;
                }
                // Zu Blob konvertieren via Canvas → ObjectURL. Same-Origin für PSV.
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext("2d");
                if (!ctx) throw new Error("Canvas-Context nicht verfügbar");
                ctx.drawImage(img, 0, 0);
                const blob: Blob = await new Promise((resolve, reject) => {
                    canvas.toBlob(
                        (b) => (b ? resolve(b) : reject(new Error("Blob-Konvertierung fehlgeschlagen"))),
                        "image/jpeg",
                        0.92,
                    );
                });
                objectUrl = URL.createObjectURL(blob);
                panoramaSrc = objectUrl;
            } catch (err) {
                console.error(
                    "[Photo360Viewer] Vor-Load (Canvas-Convert) fehlgeschlagen, versuche PSV direkt mit Original-URL:",
                    err,
                    "src=", src,
                );
                // Fallback: PSV direkt mit Original-URL versuchen (funktioniert
                // wenn kein fetch-Wrapper aktiv ist oder R2-CORS credentials setzt).
            }

            if (cancelled) return;
            try {
                viewer = new Viewer({
                    container: host,
                    panorama: panoramaSrc,
                    defaultZoomLvl: 20,
                    navbar: false,
                    mousewheel: true,
                    mousemove: true,
                    loadingImg: undefined,
                    loadingTxt: "",
                });
            } catch (err) {
                let ctxInfo = "";
                try {
                    const c = document.createElement("canvas");
                    ctxInfo = `webgl2=${!!c.getContext("webgl2")} webgl=${!!c.getContext(
                        "webgl",
                    )} exp=${!!c.getContext("experimental-webgl")}`;
                } catch {
                    /* ignore */
                }
                console.error(
                    "[Photo360Viewer] PSV init failed:",
                    err,
                    "diag:", ctxInfo,
                    "src=", src,
                );
                if (!cancelled) {
                    setStatus("error");
                    setErrorMsg(
                        err instanceof Error ? err.message : "Init fehlgeschlagen",
                    );
                }
                return;
            }

            viewer.addEventListener("ready", () => {
                window.clearTimeout(loadingTimeout);
                setStatus("ready");
            });
            viewer.addEventListener("panorama-loaded", () => {
                window.clearTimeout(loadingTimeout);
                setStatus("ready");
                // Auto-Tour: wenn Prop gesetzt, starte rAF-Rotation direkt nach
                // Panorama-Ready. 300ms Delay, damit das Panorama sichtbar ist
                // bevor die Kamera zu drehen beginnt.
                if (autoTourRef.current) {
                    window.setTimeout(() => {
                        if (!cancelled && !autoRotatingRef.current) {
                            startAutoRotate();
                        }
                    }, 300);
                }
            });
            viewer.addEventListener("panorama-error", (e) => {
                window.clearTimeout(loadingTimeout);
                console.error("[Photo360Viewer] panorama-error", e, "src=", src);
                setStatus("error");
                setErrorMsg("Panorama konnte nicht geladen werden.");
            });
            viewerRef.current = viewer;
            // Test-Hook (leicht + read-only): letzte Photo360-Instanz auf window.
            // Erlaubt harte Playwright-Assertions (yaw-delta) ohne den Viewer
            // extra instrumentieren zu müssen. In Produktion harmlos.
            try {
                (window as unknown as { __hf_photo360__?: Viewer }).__hf_photo360__ = viewer;
            } catch {
                /* ignore */
            }
        };

        initWithBlobUrl();

        return () => {
            cancelled = true;
            window.clearTimeout(loadingTimeout);
            try {
                viewerRef.current?.destroy();
            } catch {
                /* ignore */
            }
            viewerRef.current = null;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                objectUrl = null;
            }
        };
    }, [src]);

    useEffect(() => {
        const onFs = () =>
            setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener("fullscreenchange", onFs);
        return () => document.removeEventListener("fullscreenchange", onFs);
    }, []);

    const resetView = () => {
        const v = viewerRef.current;
        if (!v) {
            console.warn("[Photo360Viewer] resetView: viewer ref is null");
            return;
        }
        // Toggle: 2. Klick während laufender Rotation → nur stoppen, kein Neustart.
        if (autoRotatingRef.current) {
            console.log("[Photo360Viewer] resetView clicked → toggling auto-rotate OFF");
            stopAutoRotate();
            return;
        }
        console.log("[Photo360Viewer] resetView clicked → yaw=0 pitch=0 zoom=20 → auto-rotate");
        try {
            // PSV v5: `speed` akzeptiert Format wie "2rpm" oder eine number
            // (degrees/second) — KEIN "ms"-Suffix.
            v.animate({ yaw: 0, pitch: 0, zoom: 20, speed: "2rpm" });
        } catch (err) {
            console.warn("[Photo360Viewer] animate failed, fallback to rotate+zoom", err);
            try {
                v.rotate({ yaw: 0, pitch: 0 });
                v.zoom(20);
            } catch (err2) {
                console.error("[Photo360Viewer] reset fallback also failed", err2);
                return;
            }
        }
        // Nach ~1s (Reset-Animation) Auto-Rotate starten
        window.setTimeout(() => startAutoRotate(), 900);
    };
    const toggleFullscreen = async () => {
        const el = containerRef.current;
        if (!el) return;
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await el.requestFullscreen?.();
                tracker.onFullscreen();
            }
        } catch {
            /* ignore */
        }
    };

    if (status === "error") {
        return (
            <ViewerFallback
                testId={`${testIdPrefix}-fallback`}
                title="360°-Foto kann nicht dargestellt werden"
                message={
                    errorMsg === "WebGL wurde per QA-Flag deaktiviert"
                        ? "WebGL wurde per QA-Flag simuliert deaktiviert."
                        : "Das 360°-Foto konnte nicht geladen werden. Bitte prüfe, ob Hardware-Beschleunigung im Browser aktiviert ist, oder öffne die Seite in einem anderen Browser (Chrome, Firefox, Safari, Edge)."
                }
            />
        );
    }

    return (
        <div
            ref={containerRef}
            className={fillParent ? "hf-viewer-shell w-full h-full" : "hf-viewer-shell w-full aspect-[16/9] sm:aspect-[21/9]"}
            data-testid={`${testIdPrefix}-shell`}
        >
            <div
                ref={viewerHostRef}
                style={{ width: "100%", height: "100%" }}
                data-testid={`${testIdPrefix}-canvas`}
            />

            {status !== "ready" && (
                <div
                    className="absolute inset-0 z-[3] flex items-center justify-center pointer-events-none"
                    style={{ background: "rgba(14,23,38,0.35)" }}
                    data-testid={`${testIdPrefix}-loading`}
                >
                    <span
                        className="hf-eyebrow"
                        style={{ color: "hsl(var(--hf-gold-soft))" }}
                    >
                        Panorama wird geladen…
                    </span>
                </div>
            )}

            {/* Interaction-Hint-Overlay: permanent sichtbar sobald das
                Panorama geladen ist. Auto-Tour und Overlay laufen parallel —
                der Wrapper ist opacity 0.75, background transparent,
                pointer-events:none, blendet die Panorama-Ansicht also nicht ab
                und blockiert keine Drag/Zoom-Events. */}
            {status === "ready" && <InteractionHintOverlay testIdPrefix={testIdPrefix} />}

            <div className="hf-viewer-controls">
                <div className="hf-viewer-controls-row">
                    <button
                        type="button"
                        onClick={resetView}
                        className={
                            autoRotating
                                ? "hf-viewer-ctrl-btn hf-autorotate-active"
                                : "hf-viewer-ctrl-btn"
                        }
                        title={autoRotating ? "Auto-Tour stoppen" : "Auto-Tour starten"}
                        aria-label={
                            autoRotating
                                ? "Auto-Rotation stoppen"
                                : "Blick zurücksetzen und Auto-Tour starten"
                        }
                        aria-pressed={autoRotating}
                        data-testid={`${testIdPrefix}-btn-reset`}
                    >
                        <RotateCcw size={18} />
                        <span className="hf-viewer-ctrl-label">Tour</span>
                    </button>
                    {showGyro && (
                        <button
                            type="button"
                            onClick={toggleGyro}
                            className={
                                gyroOn
                                    ? "hf-viewer-ctrl-btn hf-viewer-ctrl-btn-gyro hf-viewer-ctrl-btn--active"
                                    : "hf-viewer-ctrl-btn hf-viewer-ctrl-btn-gyro"
                            }
                            title={gyroOn ? "Gyroskop aus" : "Gyroskop an"}
                            aria-label={gyroOn ? "Gyroskop aus" : "Gyroskop an"}
                            aria-pressed={gyroOn}
                            data-testid={`${testIdPrefix}-btn-gyro`}
                        >
                            <Compass size={18} />
                            <span className="hf-viewer-ctrl-label">Gyro</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={toggleFullscreen}
                        className="hf-viewer-ctrl-btn"
                        title={isFullscreen ? "Vollbild verlassen" : "Vollbild"}
                        aria-label={
                            isFullscreen ? "Vollbild verlassen" : "Vollbild"
                        }
                        data-testid={`${testIdPrefix}-btn-fullscreen`}
                    >
                        {isFullscreen ? (
                            <Minimize2 size={18} />
                        ) : (
                            <Maximize2 size={18} />
                        )}
                        <span className="hf-viewer-ctrl-label">Vollbild</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
